import {
  Header,
  AppLayout,
  ContentLayout,
  Grid,
  SpaceBetween,
  Button,
  Container,
  DatePicker,
  Input,
  FormField,
  Alert,
  Box,
  Select,
  Spinner,
  DateRangePickerProps,
} from '@cloudscape-design/components';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  NumberParam,
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';

import { useUrlFilterState, useUrlSortState } from '../common/filtering/filter';
import { ZappyHeader } from '../common/header';
import { OrderTable } from '../components/OrderTable';
import { ErrorMessage } from '../components/ErrorMessage';
import { ViewLayout } from '../components/ViewLayout';
import { getDashboardReport } from '../common/api/reports';

import { DashboardFilter } from './filter';
import { DashboardMetrics } from './metrics';

function Content() {
  const [now, setNow] = useState(() => new Date());

  const defaultDate = useMemo(() => {
    const current = new Date();
    const offset = current.getTimezoneOffset() * 60000;

    return new Date(current.getTime() - offset).toISOString().slice(0, -1).split('T')[0];
  }, []);

  const [
    {
      date: dateParam,
      days: daysParam,
      region: regionParam,
      base: baseParam,
      converted: convertedParam,
    },
    setDashboardFilters,
  ] = useQueryParams({
    date: withDefault(StringParam, defaultDate),
    days: withDefault(NumberParam, 30),
    region: StringParam,
    base: StringParam,
    converted: StringParam,
  });
  const [orderPage, setOrderPageParam] = useQueryParam('orderPage', withDefault(NumberParam, 1));

  const date = dateParam;
  const days = Math.max(daysParam, 1);
  const regionCurrency = regionParam === 'global' ? undefined : regionParam ?? 'CAD';
  const baseCurrency = regionCurrency ? baseParam : baseParam ?? 'CAD';
  const convertedCurrency = regionCurrency ? convertedParam : convertedParam ?? 'USD';

  const { endDate, startDate, startDateIso, endDateIso } = useMemo(() => {
    const computedEndDate = new Date(`${date}T23:59:59`);
    const computedStartDate = new Date(computedEndDate.getTime() - days * 24 * 60 * 60 * 1000);

    return {
      endDate: computedEndDate,
      startDate: computedStartDate,
      startDateIso: computedStartDate.toISOString(),
      endDateIso: computedEndDate.toISOString(),
    };
  }, [date, days]);

  const orderSort = useUrlSortState('rent_time', 'desc');
  const { query, setQuery } = useUrlFilterState();

  const [range, setRange] = useState<DateRangePickerProps.Value | null>({
    startDate: startDateIso,
    endDate: endDateIso,
    type: 'absolute',
  });

  useEffect(() => {
    setRange((current) => {
      if (
        current?.type === 'absolute' &&
        current.startDate === startDateIso &&
        current.endDate === endDateIso
      ) {
        return current;
      }

      return {
        startDate: startDateIso,
        endDate: endDateIso,
        type: 'absolute',
      };
    });
  }, [startDateIso, endDateIso]);

  const { data, isPending, isFetching, error } = useQuery({
    queryKey: ['dashboard', endDate, days, regionCurrency, baseCurrency, convertedCurrency, now],
    queryFn: () =>
      getDashboardReport(
        endDate,
        days,
        regionCurrency,
        baseCurrency ?? undefined,
        convertedCurrency ?? undefined,
      ),
    staleTime: 3_600_000, // 1 hour
  });

  const loading = (isPending || isFetching) && !error;

  const handleSetDate = (value: string) => {
    setDashboardFilters({ date: value }, 'pushIn');
  };

  const handleSetDays = (value: number) => {
    const normalized = Math.max(value, 1);
    setDashboardFilters({ days: normalized }, 'pushIn');
  };

  const handleSetRegionCurrency = (value: string | undefined) => {
    if (!value) {
      setDashboardFilters(
        {
          region: 'global',
          base: 'CAD',
          converted: 'USD',
        },
        'pushIn',
      );
    } else {
      setDashboardFilters(
        {
          region: value,
          base: undefined,
          converted: undefined,
        },
        'pushIn',
      );
    }
  };

  const handleSetBaseCurrency = (value: string | undefined) => {
    setDashboardFilters({ base: value ?? undefined }, 'pushIn');
  };

  const handleSetConvertedCurrency = (value: string | undefined) => {
    setDashboardFilters({ converted: value ?? undefined }, 'pushIn');
  };

  const handleSetOrderPage = (page: number) => {
    if (page !== orderPage) {
      setOrderPageParam(page, 'pushIn');
    }
  };

  const metricCurrency = convertedCurrency ?? regionCurrency ?? 'USD';

  return (
    <SpaceBetween direction="vertical" size="m">
      <SpaceBetween direction="vertical" size="m">
        <Container>
          <SpaceBetween direction="vertical" size="xs">
            <DashboardFilter
              date={date}
              days={days}
              loading={loading}
              refresh={() => {
                setNow(new Date());
              }}
              setDate={handleSetDate}
              setDays={handleSetDays}
              regionCurrency={regionCurrency}
              setRegionCurrency={handleSetRegionCurrency}
              baseCurrency={baseCurrency}
              setBaseCurrency={handleSetBaseCurrency}
              convertedCurrency={convertedCurrency}
              setConvertedCurrency={handleSetConvertedCurrency}
              convertedCurrencyRate={data?.report.currency_rate}
            />
            <DashboardMetrics
              days={days}
              error={error}
              loading={loading}
              report={data}
              currency={metricCurrency}
            />
          </SpaceBetween>
        </Container>
        {error ? (
          <ErrorMessage
            error={error}
            loading={loading}
            retry={() => {
              setNow(new Date());
            }}
          />
        ) : undefined}
      </SpaceBetween>
      <OrderTable
        {...orderSort}
        description={`Zappy orders within the last ${days} days`}
        now={endDate}
        page={orderPage}
        query={query}
        range={range}
        setNow={() => {
          setNow(new Date());
        }}
        setPage={handleSetOrderPage}
        setQuery={setQuery}
        setRange={setRange}
        variant="container"
        hideActions
        hideDateRange
      />
    </SpaceBetween>
  );
}

export default function DashboardPage() {
  return (
    <ViewLayout
      breadcrumbs={[{ text: 'Overview', href: '' }]}
      contentType="dashboard"
      header={<ZappyHeader title="Dashboard" />}
      headerVariant="high-contrast"
    >
      <Content />
    </ViewLayout>
  );
}
